(() => {
  'use strict';

  const runWhenIdle = (callback, timeout = 2000) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
    } else {
      window.setTimeout(callback, 250);
    }
  };

  // Analytics is non-critical. Delay it until the visible page has finished loading.
  const loadMetricool = () => {
    if (document.querySelector('script[data-libase-metricool]')) return;

    const script = document.createElement('script');
    script.src = 'https://tracker.metricool.com/resources/be.js';
    script.async = true;
    script.dataset.libaseMetricool = 'true';
    script.onload = () => {
      if (window.beTracker && typeof window.beTracker.t === 'function') {
        window.beTracker.t({ hash: 'a9a3f3f91f9b2ba41c141cd47ee65f8b' });
      }
    };
    document.head.appendChild(script);
  };

  if (document.readyState === 'complete') {
    runWhenIdle(loadMetricool);
  } else {
    window.addEventListener('load', () => runWhenIdle(loadMetricool), { once: true });
  }

  // Patch the PoingPong widget only when its nodes appear instead of scanning the full DOM twice.
  const patchPoingPongNode = (root) => {
    if (!root) return;
    const elementRoot = root.nodeType === Node.ELEMENT_NODE ? root : root.parentElement;
    if (!elementRoot) return;

    const walker = document.createTreeWalker(elementRoot, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walker.nextNode())) {
      if (/割引額：500\s*円/.test(textNode.nodeValue || '')) {
        textNode.nodeValue = textNode.nodeValue.replace(/割引額：500\s*円/g, 'ガチャ1回券');
      }
    }

    const candidates = [];
    if (elementRoot.matches?.('button, a')) candidates.push(elementRoot);
    candidates.push(...elementRoot.querySelectorAll?.('button, a') || []);

    candidates.forEach((element) => {
      if (element.dataset.libaseGachaPatched === 'true') return;
      if (element.textContent.trim() !== '適用する') return;

      element.dataset.libaseGachaPatched = 'true';
      element.textContent = '🎰 ガチャを回す';
      Object.assign(element.style, {
        background: '#111',
        color: '#fff',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '8px',
        cursor: 'pointer',
      });

      element.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const container = element.closest('[class*="coupon"], [class*="poing"], [class*="point"]') || document;
        const codeElement = container.querySelector('[class*="coupon"] input, [class*="coupon"] span, input');
        const code = codeElement ? (codeElement.value || codeElement.textContent || '').trim() : '';
        window.location.href = `/pages/gacha${code ? `?coupon=${encodeURIComponent(code)}` : ''}`;
      });
    });
  };

  const startPoingPongObserver = () => {
    patchPoingPongNode(document.body);
    let queued = false;
    const pending = new Set();

    const flush = () => {
      queued = false;
      pending.forEach(patchPoingPongNode);
      pending.clear();
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => pending.add(node));
      });
      if (!queued && pending.size) {
        queued = true;
        requestAnimationFrame(flush);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 12000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPoingPongObserver, { once: true });
  } else {
    startPoingPongObserver();
  }

  // Product-card reviews are fetched only when cards approach the viewport.
  const initLazyCardReviews = () => {
    const cards = [...document.querySelectorAll('.libase-card-review[data-product-handle]')];
    if (!cards.length) return;

    const API = 'https://libase-gacha.onrender.com';
    const cache = new Map();
    const queuedHandles = new Set();
    const queue = [];
    let active = 0;
    const maxConcurrent = 3;

    const makeStars = (average) => {
      const rounded = Math.max(0, Math.min(5, Math.round(Number(average) || 0)));
      return '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
    };

    const updateCards = (handle, data) => {
      document.querySelectorAll(`.libase-card-review[data-product-handle="${CSS.escape(handle)}"]`).forEach((card) => {
        if (!data?.ok || !data.count) return;
        const starsElement = card.querySelector('.libase-card-review-stars');
        const countElement = card.querySelector('.libase-card-review-count');
        if (!starsElement || !countElement) return;
        starsElement.textContent = makeStars(data.avg);
        countElement.textContent = `(${data.count})`;
        card.classList.remove('hidden');
      });
    };

    const pump = () => {
      while (active < maxConcurrent && queue.length) {
        const handle = queue.shift();
        active += 1;

        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 7000);
        fetch(`${API}/review-summary?productId=${encodeURIComponent(handle)}`, {
          signal: controller.signal,
          credentials: 'omit',
        })
          .then((response) => response.ok ? response.json() : null)
          .then((data) => {
            cache.set(handle, data);
            updateCards(handle, data);
          })
          .catch(() => cache.set(handle, null))
          .finally(() => {
            window.clearTimeout(timer);
            active -= 1;
            pump();
          });
      }
    };

    const enqueue = (card) => {
      const handle = card.dataset.productHandle;
      if (!handle) return;
      if (cache.has(handle)) {
        updateCards(handle, cache.get(handle));
        return;
      }
      if (queuedHandles.has(handle)) return;
      queuedHandles.add(handle);
      queue.push(handle);
      pump();
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          enqueue(entry.target);
        });
      }, { rootMargin: '500px 0px' });
      cards.forEach((card) => observer.observe(card));
    } else {
      cards.forEach(enqueue);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyCardReviews, { once: true });
  } else {
    initLazyCardReviews();
  }
})();
